<template>
  <va-card class="card-container">
    <va-card-title>
      <slot name="title">
        {{ $t("activations.title") }}
      </slot>
      <va-button
        flat
        class="upgrade-button"
        :loading="state.exporting"
        :disabled="state.exporting"
        @click="exportActivations()"
      >
        {{ $t("buttons.exportCsv") }}
      </va-button>
      <va-button flat @click="openEventsLogs()">
        {{ $t("buttons.showLogs") }}
      </va-button>
    </va-card-title>
    <va-card-content>
      <va-data-table
        :no-data-html="$t('activations.noItems')"
        :items="state.activations"
        :columns="state.columns"
      >
        <template #cell(createdAt)="{source: createdAt}">
          {{ createdAt ? printDate(createdAt) : "Never" }}
        </template>
        <template #header(lastSynchronizarion)>
          {{ $t("activations.lastSync") }}
        </template>

        <template #header(aliasName)>
          {{ $t("activations.aliasName") }}
        </template>

        <template #cell(aliasName)="{ source: aliasName }">
          {{ aliasName || "--" }}
        </template>

        <template #cell(user)="{ source: user }">
          <template v-if="user">
            {{ user }}
          </template>
          <span v-else>--</span>
        </template>

        <template #cell(lastSynchronizarion)="{ source: lastSynchronizarion }">
          {{ printDate(lastSynchronizarion) }}
        </template>

        <template #cell(restrictedMode)="{ source: restrictedMode }">
          <va-badge
            :text="restrictedMode ? 'Yes' : 'No'"
            :color="restrictedMode ? 'success' : 'error'"
          />
        </template>

        <template #cell(deactivate)="{ rowIndex }">
          <va-button
            v-if="!isRestrictedNVivoTrial(entitlement)"
            color="#DC2626"
            flat
            icon="delete"
            @click="openDeactivationModal(rowIndex)"
          />
        </template>
      </va-data-table>
    </va-card-content>
    <va-card-content>
      <activations-offline
        :entitlement-id="entitlementId"
        :license-key="licenseKey"
        @add="loadActivations(1, true)"
      />

      <va-pagination
        class="pagination"
        input
        color="#000000"
        v-model="state.currentPage"
        :hide-on-single-page="true"
        :pages="state.totalPages"
        :page-size="state.perPage"
      />
    </va-card-content>
  </va-card>
  <show-activations-events-modal
    id="eeee"
    :isOpen="state.showActivationsEventsModalIsOpen"
    @cancel="state.showActivationsEventsModalIsOpen = false"
    @validate="validate()"
  />
</template>
<style scoped>
.card-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title {
  flex: 1; /* Ensures title takes up all available space */
}

.upgrade-button {
  margin-left: auto; /* Pushes the button to the right */
}
</style>
<script setup>
import { computed, reactive, watch, onMounted, inject } from "vue";
import {
  callFetchActivations,
  callFetchAllActivations,
  callDeleteActivation
} from "@/api/licensing";
import { printDate, isRestrictedNVivoTrial } from "@/utils";
import { toCsv, toIsoUtc, downloadCsv } from "@/utils/csv";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import ShowActivationsEventsModal from "@/components/modal/ShowActivationsEventsModal.vue";
import ActivationsOffline from "./ActivationsOffline.vue";

const { t } = useI18n();
const store = useStore();
const toasts = inject("toasts");
const handleError = inject("handleError");

// Access entitlement data from store
const entitlement = computed(() => store.state.licensing.entitlementEdit);

const props = defineProps({
  entitlementId: String
});

const state = reactive({
  activations: [],
  columns: computed(() => {
    if (state.activations[0] == null) {
      return [];
    }
    const columns = Object.keys(state.activations[0]).map(key => {
      return { key };
    });
    // Only add deactivate column if not a restricted NVivo trial
    if (!isRestrictedNVivoTrial(entitlement.value)) {
      columns.push({ key: "deactivate" });
    }
    return columns;
  }),
  loading: false,
  lastLoadedPage: undefined,
  currentPage: 1,
  totalItems: 0,
  perPage: 100,
  totalPages: 0,
  deactivatingId: "",
  showDeactivationModal: computed(() => !!state.deactivatingId),
  showActivationsEventsModalIsOpen: false,
  exporting: false
});

// Columns of the CSV export, in the order requested by the business
const EXPORT_COLUMNS = [
  { labelKey: "activationId", value: activation => activation?.id },
  { labelKey: "licenseId", value: activation => activation?.licenseId },
  { labelKey: "entitlementId", value: activation => activation?.entitlementId },
  { labelKey: "createdAt", value: activation => toIsoUtc(activation?.createdAt) },
  {
    labelKey: "lastRefreshedAt",
    value: activation => toIsoUtc(activation?.lastRefreshedAt)
  },
  { labelKey: "method", value: activation => activation?.method },
  { labelKey: "userName", value: activation => activation?.user?.name },
  { labelKey: "userEmail", value: activation => activation?.user?.email },
  { labelKey: "productVersion", value: activation => activation?.productVersion },
  { labelKey: "os", value: activation => activation?.os },
  { labelKey: "osVersion", value: activation => activation?.osVersion },
  { labelKey: "hostName", value: activation => activation?.hostName }
];

const openEventsLogs = () => {
  state.showActivationsEventsModalIsOpen = true;
};
const validate = () => {
  state.showActivationsEventsModalIsOpen = false;
};
async function loadActivations(page = 1, forceReload = false) {
  if (
    (state.loading === true || page === state.lastLoadedPage) &&
    !forceReload
  ) {
    return;
  }

  /**
   * Wait 5s
   */
  if (forceReload) {
    await new Promise(resolve => setTimeout(resolve, 5000));
  }

  state.lastLoadedPage = page;
  state.loading = true;

  const params = {
    count: state.perPage,
    page,
    // licenseId: route.params.id,
    entitlementId: props.entitlementId
  };

  try {
    const resp = await callFetchActivations({ params });
    //console.log(resp)
    if (resp?.data) {
      state.activations = resp.data.items.map(item => {
        return {
          createdAt: item?.createdAt,
          lastSynchronizarion: item?.lastRefreshedAt,
          os: item?.os,
          osVersion: item?.osVersion,
          hostName: item?.hostName,
          aliasName: item?.aliasName,
          macAddress: item?.metadata?.MACAddress,
          user: item?.user?.name,
          restrictedMode: item?.metadata?.RestrictedMode === "true",
          id: item?.id
        };
      });
      state.totalItems = resp.data.total;
      state.totalPages =
        Math.floor(resp.data.total / state.perPage) +
        (resp.data.total % state.perPage === 0 ? 0 : 1);
      state.currentPage = page;
    }
  } catch (e) {
    handleError(e?.response?.data);
  }

  state.loading = false;
}

// Fetches page by page so the export covers every activation, not just the displayed page
async function exportActivations() {
  if (state.exporting) {
    return;
  }

  state.exporting = true;

  try {
    const activations = await callFetchAllActivations({
      params: { entitlementId: props.entitlementId }
    });

    if (activations.length === 0) {
      toasts?.info(t("activations.exportNoData"));
      return;
    }

    const rows = [
      EXPORT_COLUMNS.map(column =>
        t(`activations.showActivationsEventsDetailModal.${column.labelKey}`)
      ),
      ...activations.map(activation =>
        EXPORT_COLUMNS.map(column => column.value(activation))
      )
    ];

    const exportedOn = new Date().toISOString().slice(0, 10);
    downloadCsv(
      `activations-${props.entitlementId}-${exportedOn}.csv`,
      toCsv(rows)
    );
  } catch (e) {
    handleError(e?.response?.data);
  } finally {
    state.exporting = false;
  }
}

const openDeactivationModal = rowIndex => {
  state.deactivatingId = state.activations[rowIndex].id;
  deactivateItem();
};

const deactivateItem = async () => {
  try {
    await callDeleteActivation(state.deactivatingId);

    // remove it from the state
    const itemIndex = state.activations.findIndex(
      el => el.id === state.deactivatingId
    );
    state.activations.splice(itemIndex, 1);
    toasts?.info(t("activations.deactivateSuccess"));
  } catch (err) {
    toasts?.error(err);
  }
  state.deactivatingId = "";
  state.deactivatingId = "";
};

watch(
  () => state.currentPage,
  async currentPage => {
    await loadActivations(currentPage);
  }
);

onMounted(async () => {
  await loadActivations();
});
</script>
